import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import styles from "./TallySyncShared.module.scss";

const TALLY_PAGE_WIDTH = 1120;
const TALLY_PAGE_HEIGHT = 712;

interface TallyResponsiveFrameProps {
  children: ReactNode;
}

export default function TallyResponsiveFrame({
  children,
}: TallyResponsiveFrameProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [hasMeasured, setHasMeasured] = useState(false);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const updateScale = () => {
      const { clientWidth, clientHeight } = viewport;
      if (clientWidth <= 0 || clientHeight <= 0) return;

      const nextScale = Math.min(
        clientWidth / TALLY_PAGE_WIDTH,
        clientHeight / TALLY_PAGE_HEIGHT,
        1
      );

      setScale((currentScale) =>
        Math.abs(currentScale - nextScale) < 0.001
          ? currentScale
          : nextScale
      );
      setHasMeasured(true);
    };

    updateScale();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScale);
      return () => window.removeEventListener("resize", updateScale);
    }

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(viewport);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div ref={viewportRef} className={styles.fitViewport}>
      <div
        className={styles.fitStage}
        style={{
          width: TALLY_PAGE_WIDTH * scale,
          height: TALLY_PAGE_HEIGHT * scale,
          visibility: hasMeasured ? "visible" : "hidden",
        }}
      >
        <div
          className={styles.fitCanvas}
          style={{ transform: `scale(${scale})` }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
